import {
  NoPreloading,
  RouterModule,
  Route as RouterRoute,
  Routes as RouterRoutes,
} from '@angular/router';
import { OktaAuthGuard, OktaCallbackComponent } from '@okta/okta-angular';
import { CanDeactivateGuard, VoucherGuard } from './guards/_index';
import { MaintenanceDowntimeComponent, NotfoundComponent, SupportComponent } from './pages/_index';

import { NgModule } from '@angular/core';
import { environment } from '@environments/environment';
import { AppLayoutComponent } from './_layout/_index';
import { ExternalUrlLoaderComponent } from './components/_index';
import { RouteEnum } from './enums/_index';

export type RoutesType = Record<RouteEnum, { label?: string; routerConfig: RouterRoute }>;

export const routes: RoutesType = {
  [RouteEnum.AGENT_PLATFORM]: {
    label: 'Agent-Platform',
    routerConfig: {
      path: 'agent-platform',
      redirectTo: 'subscription/agent-platform',
    },
  },
  [RouteEnum.AP_NOV2024_ENGLISH]: {
    label: 'AP-Nov2024-English',
    routerConfig: {
      path: 'subscription/ap-nov2024-english',
      component: ExternalUrlLoaderComponent,
      data: { externalUrl: `${environment.externalRedirect.urls[0]}` },
    },
  },
  [RouteEnum.AP_SPANISH]: {
    label: 'AP-Spanish',
    routerConfig: {
      path: 'subscription/ap-spanish',
      component: ExternalUrlLoaderComponent,
      data: { externalUrl: `${environment.externalRedirect.urls[1]}` },
    },
  },
  [RouteEnum.AP_PORTUGUESE]: {
    label: 'AP-Portuguese',
    routerConfig: {
      path: 'subscription/ap-portuguese',
      component: ExternalUrlLoaderComponent,
      data: { externalUrl: `${environment.externalRedirect.urls[2]}` },
    },
  },
  [RouteEnum.AP_JAPANESE]: {
    label: 'AP-Japanese',
    routerConfig: {
      path: 'subscription/ap-japanese',
      component: ExternalUrlLoaderComponent,
      data: { externalUrl: `${environment.externalRedirect.urls[3]}` },
    },
  },
  [RouteEnum.ANNOUNCEMENT]: {
    label: 'Announcement',
    routerConfig: {
      path: 'announcement/:slug',
      loadChildren: () =>
        import('./pages/announcement/announcement.module').then((m) => m.AnnouncementModule),
    },
  },
  [RouteEnum.BEYOND_CX]: {
    label: 'Beyond_CX',
    routerConfig: {
      path: 'beyond-cx',
      redirectTo: 'subscription/beyond-cx',
    },
  },
  [RouteEnum.CERTIFICATIONS_TRAINING_GENESYS_TECHNICAL_CERTIFICATION_GUIDE]: {
    label: 'Certifications-Training-Genesys-Technical-Certification-Guide',
    routerConfig: {
      path: 'certification/genesys-technical-certification-guide',
      redirectTo: 'certification/home',
    },
  },
  [RouteEnum.CERTIFICATIONS_TRAINING_GENESYS_TECHNICAL_CERTIFICATION_GUIDE_REDIRECT]: {
    label: 'Certifications-Training-Genesys-Technical-Certification-Guide-Redirect',
    routerConfig: {
      path: 'genesys-technical-certification-guide',
      redirectTo: 'certification/home',
    },
  },
  [RouteEnum.CERTIFICATIONS_TRAINING]: {
    label: 'Certifications-Training',
    routerConfig: {
      path: 'certification/:slug',
      loadChildren: () =>
        import('./pages/certifications-training/certifications-training.module').then(
          (m) => m.CertificationsTrainingModule
        ),
    },
  },
  [RouteEnum.COURSE]: {
    label: 'Course',
    routerConfig: {
      path: 'course/:slug',
      loadChildren: () =>
        import('./pages/course-detail/course-detail.module').then((m) => m.CourseDetailModule),
    },
  },
  [RouteEnum.DASHBOARD]: {
    label: 'Dashboard',
    routerConfig: {
      path: 'dashboard',
      loadChildren: () =>
        import('./pages/dashboard/dashboard.module').then((m) => m.DashboardModule),
      canActivate: [OktaAuthGuard],
    },
  },
  [RouteEnum.HOME_EMPTY_PATH]: {
    label: 'Home_Empty_Path',
    routerConfig: {
      path: '',
      loadChildren: () => import('./pages/home/home.module').then((m) => m.HomeModule),
      pathMatch: 'full',
    },
  },
  [RouteEnum.HOME]: {
    label: 'Home',
    routerConfig: {
      // Warning: This path maps to a field in Okta's Admin dashboard.
      // Do not change it without updating the Okta app (done through IT).
      path: 'home',
      loadChildren: () => import('./pages/home/home.module').then((m) => m.HomeModule),
    },
  },
  [RouteEnum.LEARNING_PATH_REDIRECT]: {
    label: 'Learning-Path-Redirect',
    routerConfig: {
      path: 'learning-path',
      redirectTo: 'learning-path/home',
    },
  },
  [RouteEnum.LEARNING_PATH]: {
    label: 'Learning-Path',
    routerConfig: {
      path: 'learning-path/:slug',
      loadChildren: () =>
        import('./pages/learning-paths/learning-paths.module').then((m) => m.LearningPathsModule),
    },
  },
  [RouteEnum.MESSAGE_INBOX]: {
    label: 'Message-Inbox',
    routerConfig: {
      path: 'message-inbox',
      loadChildren: () =>
        import('./pages/message-inbox/message-inbox.module').then((m) => m.MessageInboxModule),
      canActivate: [OktaAuthGuard],
    },
  },
  [RouteEnum.PRODUCTS]: {
    label: 'Products',
    routerConfig: {
      path: 'products',
      loadChildren: () => import('./pages/products/products.module').then((m) => m.ProductsModule),
    },
  },
  [RouteEnum.SEARCH]: {
    label: 'Search',
    routerConfig: {
      path: 'search',
      loadChildren: () => import('./pages/search/search.module').then((m) => m.SearchModule),
    },
  },
  [RouteEnum.SUBSCRIPTION_GENESYS_CLOUD]: {
    label: 'Subscription-Genesys-Cloud',
    routerConfig: {
      path: 'genesys-cloud-learning-subscription',
      redirectTo: 'subscription/genesys-cloud-learning-subscription',
    },
  },
  [RouteEnum.SUBSCRIPTION_AI_SUB]: {
    label: 'Subscription-Ai-Sub',
    routerConfig: {
      path: 'ai-essentials',
      redirectTo: 'subscription/ai-essentials',
    },
  },
  [RouteEnum.SUBSCRIPTION_GENESYS_ENGAGE]: {
    label: 'Subscription-Genesys-Engage',
    routerConfig: {
      path: 'genesys-engage-learning-subscription',
      redirectTo: 'subscription/genesys-engage-learning-subscription',
    },
  },
  [RouteEnum.SUBSCRIPTION_POINTILLIST_REDIRECT]: {
    label: 'Subscription-Pointillist-Redirect',
    routerConfig: {
      path: 'pointillist',
      redirectTo: 'subscription/pointillist-learning-subscription',
    },
  },
  [RouteEnum.SUBSCRIPTION_POINTILLIST]: {
    label: 'Subscription-Pointillist',
    routerConfig: {
      path: 'pointillist-learning-subscription',
      redirectTo: 'subscription/pointillist-learning-subscription',
    },
  },
  [RouteEnum.SUBSCRIPTION_PURECONNECT]: {
    label: 'Subscription-Pureconnect',
    routerConfig: {
      path: 'pureconnect-learning-subscription',
      redirectTo: 'subscription/pureconnect-learning-subscription',
    },
  },
  [RouteEnum.SUBSCRIPTION]: {
    label: 'Subscription',
    routerConfig: {
      path: 'subscription/:id',
      loadChildren: () =>
        import('./pages/subscription-detail/subscription-detail.module').then(
          (m) => m.SubscriptionDetailModule
        ),
    },
  },
  [RouteEnum.TERMS_AND_CONDITIONS]: {
    label: 'Terms-And-Conditions',
    routerConfig: {
      path: 'terms-and-conditions',
      loadChildren: () =>
        import('./pages/terms-and-conditions/terms-and-conditions.module').then(
          (m) => m.TermsAndConditionsModule
        ),
      data: { slug: 'terms-and-conditions' },
    },
  },
  [RouteEnum.TERMS_AND_CONDITIONS_2022]: {
    label: 'Terms-And-Conditions-2022',
    routerConfig: {
      path: 'terms-and-conditions-2022',
      loadChildren: () =>
        import('./pages/terms-and-conditions/terms-and-conditions.module').then(
          (m) => m.TermsAndConditionsModule
        ),
      data: { slug: 'terms-and-conditions-2022' },
    },
  },
  [RouteEnum.TRAINING_SUPPORT_LEGACY_ELEARNING_GENESYS_CLOUD]: {
    label: 'Training-Support-Legacy-Genesys-Cloud',
    routerConfig: {
      path: 'genesys-cloud-elearning',
      redirectTo: 'genesys-cloud-cx-elearning',
    },
  },
  [RouteEnum.TRAINING_SUPPORT_GENESYS_CLOUD]: {
    label: 'Training-Support-Genesys-Cloud',
    routerConfig: {
      path: 'training-support/genesys-cloud',
      redirectTo: 'genesys-cloud-cx-elearning',
    },
  },
  [RouteEnum.TRAINING_SUPPORT_GENESYS_CLOUD_CX_ELEARNING]: {
    label: 'Training-Support-Genesys-Cloud-CX-Elearning',
    routerConfig: {
      path: 'training-support/genesys-cloud-cx-elearning',
      redirectTo: 'genesys-cloud-cx-elearning',
    },
  },
  [RouteEnum.TRAINING_SUPPORT_GENESYS_DX_ELEARNING]: {
    label: 'Training-Support-Genesys-DX-Elearning',
    routerConfig: {
      path: 'training-support/genesys-dx-elearning',
      redirectTo: 'genesys-dx-elearning',
    },
  },
  [RouteEnum.TRAINING_SUPPORT_Quick_Start_Guide]: {
    label: 'Training-Support-Quick-Start-Guide',
    routerConfig: {
      path: 'training-support/quick-start-guide',
      redirectTo: 'training-support/getting-started',
    },
  },
  [RouteEnum.TRAINING_SUPPORT_ELEARNING_GENESYS_DX]: {
    label: 'Training-Support-Elearning-Genesys-DX',
    routerConfig: {
      path: 'genesys-dx-elearning',
      loadChildren: () =>
        import(
          './pages/training-support-elearning-page/training-support-elearning-page.module'
        ).then((m) => m.TrainingSupportELearningPageModule),
      data: { slug: 'genesys-dx-elearning' },
    },
  },
  [RouteEnum.TRAINING_SUPPORT_ELEARNING_GENESYS_CLOUD_CX]: {
    label: 'Training-Support-Elearning-Genesys-Cloud-CX',
    routerConfig: {
      path: 'genesys-cloud-cx-elearning',
      loadChildren: () =>
        import(
          './pages/training-support-elearning-page/training-support-elearning-page.module'
        ).then((m) => m.TrainingSupportELearningPageModule),
      data: { slug: 'genesys-cloud-cx-elearning' },
    },
  },
  [RouteEnum.TRAINING_SUPPORT]: {
    label: 'Training-Support',
    routerConfig: {
      path: 'training-support/:slug',
      loadChildren: () =>
        import('./pages/quick-start-quide/quick-start-guide.module').then(
          (m) => m.QuickStartGuideModule
        ),
    },
  },
  [RouteEnum.VIDEO_DETAIL]: {
    label: 'Video-Detail',
    routerConfig: {
      path: 'video/:slug',
      loadChildren: () =>
        import('./pages/video-detail/video-detail.module').then((m) => m.VideoDetailModule),
    },
  },
  [RouteEnum.VIDEOS]: {
    label: 'Videos',
    routerConfig: {
      path: 'videos',
      loadChildren: () => import('./pages/videos/videos.module').then((m) => m.VideosModule),
    },
  },
  [RouteEnum.VOUCHER_REDEMPTION]: {
    label: 'Voucher-Redemption',
    routerConfig: {
      path: 'voucher-redemption',
      loadChildren: () =>
        import('./pages/voucher-redemption/voucher-redemption.module').then(
          (m) => m.VoucherRedemptionModule
        ),
      canActivate: [VoucherGuard],
      canDeactivate: [CanDeactivateGuard],
    },
  },
};

export const routerRoutes: RouterRoutes = Object.keys(routes).map((key) => {
  const route = routes[key];
  return route.routerConfig;
});

@NgModule({
  imports: [
    RouterModule.forRoot(
      [
        {
          path: '',
          component: AppLayoutComponent,
          children: [...routerRoutes],
        },
        {
          path: 'login',
          loadChildren: () => import('./pages/login/login.module').then((m) => m.LoginModule),
        },
        {
          // Warning: This path maps to a field in Okta's admin dashboard.
          // Do not change it without updating the Okta app (done through IT).
          path: 'login/callback',
          component: OktaCallbackComponent,
        },
        {
          path: 'maintenance',
          component: MaintenanceDowntimeComponent,
        },
        {
          path: 'support',
          component: SupportComponent,
        },
        {
          path: 'pagenotfound',
          component: NotfoundComponent,
        },
        {
          path: '**',
          component: NotfoundComponent,
        },
      ],
      {
        preloadingStrategy: NoPreloading, // lazy load all modules
      }
    ),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
